import React, { useEffect, useState } from 'react';

import { config } from 'config/config';
import routePaths from 'config/routes';
import moment from 'moment';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import apiPaths from 'services/apiPaths';
import { privateRoute } from 'services/privateRoute';
import {
  setBreadCrumbs,
  setFirstCompletedProfile,
  updateChallenges,
} from 'src/actions/appActions';
import { getBreadCrumbs, NotFound } from 'src/components';
import { builderChallenge } from 'src/components/challenge/builder/BuilderChallenge';
import CustomBuildPageChallenge, {
  showMoreButton,
} from 'src/components/challenge/CustomChallengeBuildPage';
import ChallengeCard from 'src/components/challenge/ChallengeCard';
import { IRootReducers } from 'src/reducers';
import { PlatformEnums, OrderTagFilter } from 'src/shared/enums';
import {
  ChallengeCardButton,
  ChallengeDetail,
  IChallenge,
  ITag,
} from 'src/shared/models';
import { appTypes } from 'src/types/appTypes';
import {
  fetchInitialData,
  getTranslatedData,
  renderChallengeCard,
} from 'utils';
import { fetchChallengeTags } from 'src/actions/appActions';
import { ReduxPageContext } from 'src/store';

const { GET_CHALLENGES } = apiPaths.CHALLENGES;
const { DEFAULT_TIMEZONE } = config.APP;

interface OwnProps {
  children?: any;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

export const renderChallengeContest = (
  challenge: ChallengeDetail
): JSX.Element => {
  const { formatMessage } = useIntl();
  let userStatus: { idResource: number; status: number };
  const challengeCardButton: ChallengeCardButton = {
    title: formatMessage({ id: 'experience.see' }),
    path: routePaths.PAGES.CHALLENGE_DETAIL,
    className: 'challengeCard__link challengeCard__link--active',
  };

  const config: ChallengeDetail = {
    ...challenge,
    challengeName: getTranslatedData(challenge, 'name'),
    challengeDescriptionShort: getTranslatedData(challenge, 'descriptionShort'),
    challengeDescription:
      getTranslatedData(challenge, 'description') ||
      getTranslatedData(challenge, 'descriptionLong'),
    challengeCardButton,
    userStatus,
    dontShowLike: true,
    dontShowDates: true,
    cardImage: challenge.cardImageMobile,
  };

  if (
    moment
      .utc(config.challengeDates.startDate, moment.ISO_8601)
      .utcOffset(DEFAULT_TIMEZONE, false)
      .isAfter(moment.utc(Date.now()).utcOffset(DEFAULT_TIMEZONE, false))
  )
    config.challengeCardButton = null;

  return <ChallengeCard {...config} />;
};

const ChallengesPage: NextPage<Props> = ({
  contestsList,
  setBreadCrumbs,
  updateChallenges,
  setFirstCompletedProfile,
  firstCompletedProfile,
  tagList,
}): JSX.Element => {
  const CONTESTS_PER_PAGE: number = 6;
  const { formatMessage } = useIntl();
  const { MOST_POPULAR, LEAST_TO_MOST_POINTS, MOST_TO_LEAST_POINTS } =
    OrderTagFilter;
  const router = useRouter();
  const PAGE_LENGTH = 6;
  const [filters, setFilters] = useState<string>();
  const [order, setOrder] = useState<number>(1);
  const [tags, setTags] = useState<ITag[]>(tagList);
  const [canShowMore, setCanShowMore] = useState<boolean>(
    contestsList.length <= PAGE_LENGTH ? false : true
  );
  const [page, setPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [tagContests, setTagContests] = useState(contestsList);

  const handleShowMore = async () => {
    setCanShowMore(
      tagContests.length <= PAGE_LENGTH * (page + 1) ? false : true
    );
    setPage(page + 1);
    updateChallenges({ contests: handleOrder(order, contestsList) });
  };

  const orderChallengesBySections = (challenges) => {
    let pendingChallenges = [];
    let usedRepeatableChallenges = [];
    let outOfStockChallenges = [];
    let completedChallenges = [];
    let finishedChallenges = [];
    let unclassifiedChallenges = [];
    let result = [];
    challenges.forEach((challenge) => {
      if (
        challenge.headboardConfiguration.availableReplays === 0 &&
        challenge.challengeUserLabel !== 'contest-completed' &&
        challenge.challengeStatusLabel !== 'expired'
      ) {
        if (challenge.challengeStatusLabel === 'out-of-stock-consumer') {
          outOfStockChallenges.push(challenge);
        } else {
          pendingChallenges.push(challenge);
        }
      } else if (
        challenge.challengeStatusLabel !== 'expired' &&
        challenge.headboardConfiguration.isChallengeReplayable
      ) {
        if (challenge.challengeStatusLabel === 'out-of-stock-consumer') {
          outOfStockChallenges.push(challenge);
        } else {
          usedRepeatableChallenges.push(challenge);
        }
      } else if (challenge.challengeUserLabel === 'contest-completed') {
        completedChallenges.push(challenge);
      } else if (
        challenge.challengeUserLabel !== 'contest-completed' &&
        challenge.challengeStatusLabel === 'expired'
      ) {
        finishedChallenges.push(challenge);
      } else {
        unclassifiedChallenges.push(challenge);
      }

      result = [
        ...pendingChallenges,
        ...usedRepeatableChallenges,
        ...outOfStockChallenges,
        ...completedChallenges,
        ...finishedChallenges,
        ...unclassifiedChallenges,
      ];
    });
    return result;
  };

  const handleOrder = (order, challenges) => {
    let initialOrder = [];
    let newOrder = [];

    switch (order) {
      case MOST_POPULAR:
      default:
        initialOrder = [...challenges].sort((a, b) =>
          a.headboardConfiguration.order < b.headboardConfiguration.order
            ? 1
            : -1
        );
        newOrder = orderChallengesBySections(initialOrder);
        break;
      case LEAST_TO_MOST_POINTS:
        initialOrder = [...challenges].sort((a, b) =>
          a.challengePoints > b.challengePoints ? 1 : -1
        );
        newOrder = orderChallengesBySections(initialOrder);
        break;
      case MOST_TO_LEAST_POINTS:
        initialOrder = [...challenges].sort((a, b) =>
          a.challengePoints < b.challengePoints ? 1 : -1
        );
        newOrder = orderChallengesBySections(initialOrder);
        break;
    }
    return newOrder;
  };

  const handleOnChangeOrder = (event) => {
    if (event) {
      setOrder(event.target.value);
      updateChallenges({
        contests: handleOrder(event.target.value, contestsList),
      });
    }
    onScroll();
  };

  const renderContests = (contests): JSX.Element | JSX.Element[] => {
    let filteredContests: ChallengeDetail[] = [];
    let contestsToRender: ChallengeDetail[] = [];

    const customMessage = 'page.contest.no-found';
    if (!filters || filters === '1') {
      contestsToRender = contests;
    } else {
      contests?.forEach((contest) => {
        if (contest.tags) {
          contest.tags.forEach((tag) => {
            if (tag.tag.idTag.toString() === filters) {
              filteredContests.push(contest);
            }
          });
          contestsToRender = filteredContests;
        }
      });
    }

    if (!contestsToRender.length) return <NotFound message={customMessage} />;

    return contestsToRender.map((challenge: ChallengeDetail, index: number) => {
      if (index <= page * CONTESTS_PER_PAGE - 1) {
        return renderChallengeCard({ challenge });
      }
    });
  };

  const handleChangeTab = (key: string) => {
    setPage(1);
    setFilters(key);
    if (key) {
      if (key !== '1') {
        const showButton =
          contestsList.filter(
            (experience) =>
              experience.tags.length &&
              experience.tags.some((tag) => String(tag?.tag?.idTag) === key)
          ).length <= PAGE_LENGTH;
        setTagContests(
          contestsList.filter(
            (experience) =>
              experience.tags.length &&
              experience.tags.some((tag) => String(tag?.tag?.idTag) === key)
          )
        );
        if (!showButton !== canShowMore) {
          setCanShowMore(!showButton);
        }
      } else {
        setTagContests(contestsList);
        setCanShowMore(contestsList.length <= PAGE_LENGTH ? false : true);
      }
    }
  };

  useEffect(() => {
    const breadCrumbs = getBreadCrumbs(router, formatMessage);
    setTimeout(() => {
      setBreadCrumbs(breadCrumbs);
    }, 20);

    // clear breadcrumbs on unmount component
    return () => {
      // TODO: Remove setTimeout => check store.ts
      setTimeout(() => {
        setBreadCrumbs([]);
      }, 0);
    };
  }, [contestsList]);

  useEffect(() => {
    if (firstCompletedProfile) {
      setFirstCompletedProfile(false);
    }
  }, [firstCompletedProfile]);

  const showMoreComponent = showMoreButton(
    `page.marketing-materials.show-more`,
    isLoadingMore,
    canShowMore,
    handleShowMore
  );

  const onScroll = () => {
    const elem = document.getElementById('titleElement');
    elem.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'center',
    });
  };

  return (
    <>
      <div
        id="titleElement"
        className={`container challengePage experiencesPage`}
      >
        <div className="container">
          <div className="experience-distributor__containerTitleDescription">
            <div className="title experience-distributor__title">
              {formatMessage({ id: 'contest.distributor-title' })}
            </div>
            <div className="regular-body experience-distributor__description">
              {formatMessage({ id: 'contest.distributor-description' })}
            </div>
          </div>
        </div>
      </div>
      {tags && (
        <CustomBuildPageChallenge
          pageClassName="experiencesPage"
          onChangeTab={handleChangeTab}
          onScroll={onScroll}
          order={order}
          handleChangeOrder={handleOnChangeOrder}
          renderChallenges={renderContests}
          showMoreComponent={showMoreComponent}
          filterComponent={null}
          tabs={tags}
          challenges={handleOrder(order, contestsList)}
        />
      )}
    </>
  );
};

/*-------------------------------------------*/
/*------------ GET INITIAL PROPS ------------*/
/*-------------------------------------------*/

const initialAction = async (
  accessToken: string,
  ctx: ReduxPageContext
): Promise<{ data: unknown }> => {
  try {
    // TODO:Move 6 to config/config.ts
    fetchChallengeTags(ctx);

    const challenges = await fetchInitialData(
      accessToken,
      `${GET_CHALLENGES}`,
      ctx
    );

    const contests: ChallengeDetail[] = challenges.map(
      (challenge: IChallenge) =>
        builderChallenge(challenge, ctx.store.getState().auth.user)
    );

    if (contests)
      ctx.store.dispatch({
        type: appTypes.APP_UPDATE_CHALLENGES,
        payload: { contests },
      });

    return { data: [] };
  } catch (error) {
    console.error('SERVER ERROR: ', error);
    return { data: null };
  }
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    firstCompletedProfile: state.app.firstCompletedProfile,
    contestsList: state.app.challenges.contests,
    tagList: state.app.tagList,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      updateChallenges,
      setBreadCrumbs,
      setFirstCompletedProfile,
      fetchChallengeTags,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  privateRoute({
    WrappedComponent: ChallengesPage,
    initialAction,
    platforms: [PlatformEnums.CON],
  })
);
